import * as React from 'react';
import { Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CARD_BORDER, DERIVED_HINT, DRAFT_GROUND, NOTE_TEXT } from '../colors';

type Props = {
  label: string;
  /**
   * The static line under the field — "Odvozeno z klíčových témat…". Copy, not
   * provenance data: the partner sends no per-field origin, and the build plan
   * settles that these sentences are simply printed.
   */
  caption?: string;
  /**
   * Draws the caption as the bordered note with an icon that the frame uses
   * where the line is about what Klara did, rather than about what the field
   * is for.
   */
  captionAsNote?: boolean;
  /**
   * Whether the value in this field is Klara's, untouched. The ground is the
   * marker: tinted while her text stands, plain once it is the user's. Default
   * true, which is what Zadání kurzu is — a screen made of nothing else.
   */
  drafted?: boolean;
  children: React.ReactNode;
};

/**
 * A field Klara drafted: label, tinted ground, provenance line.
 *
 * The tint says whose words these are. On Zadání kurzu every field is hers, so
 * the whole screen is tinted; on Vstupy the two halves sit side by side and
 * the ground is what tells them apart — which is the whole point of the marker
 * there. The plain state is white with a hairline, as the frame draws it, not
 * the grey the review guessed at.
 *
 * The editors inside keep their own behaviour and lose only their chrome: the
 * underline would draw a second field boundary inside a box that already is
 * one.
 */
export function DerivedField({
  label,
  caption,
  captionAsNote,
  drafted = true,
  children,
}: Props) {
  return (
    <Box sx={{ marginBottom: '20px' }}>
      <Typography
        sx={{ fontWeight: 500, fontSize: '14px', color: 'var(--color-black)' }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          marginTop: '6px',
          padding: '14px 8px 8px 18px',
          borderRadius: '4px',
          backgroundColor: drafted ? DRAFT_GROUND : 'var(--color-white)',
          // The tinted field draws its border in its own colour, so only the
          // plain one needs a visible edge.
          border: drafted ? 'none' : `1px solid ${CARD_BORDER}`,
          boxShadow: '0 4px 10px 1px rgba(0, 0, 0, 0.25)',
          // The box is the field boundary, so the inputs inside give theirs up
          // — MUI draws the underline as ::before, and the focus one as
          // ::after.
          '& .MuiInput-root:before, & .MuiInput-root:after': {
            display: 'none',
          },
          '& .MuiInputBase-input': {
            fontSize: '14px',
            lineHeight: '22.4px',
            color: 'var(--color-black)',
          },
          // A disabled field is still Klara's text and has to stay readable;
          // MUI greys it to the point of disappearing on this tint.
          '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: 'var(--color-black)',
          },
        }}
      >
        {children}
      </Box>

      {caption && !captionAsNote && (
        <Typography sx={{ fontSize: '12px', color: DERIVED_HINT, marginTop: '6px' }}>
          {caption}
        </Typography>
      )}

      {caption && captionAsNote && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '6px',
            padding: '9px 13px 11px',
            borderRadius: '8px',
            border: `1px solid ${CARD_BORDER}`,
            backgroundColor: 'var(--color-background)',
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: '16px', color: NOTE_TEXT }} />
          <Typography sx={{ fontSize: '13px', color: NOTE_TEXT }}>
            {caption}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
